import {Stack, styled, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useEffect, useState} from "react";
import {
    createContainer,
    VictoryAxis,
    VictoryChart,
    VictoryLabel,
    VictoryLegend,
    VictoryLine,
    VictoryTheme,
    VictoryTooltip
} from "victory";
import {ItemPaper} from "./WeaponBuilder";
import AdvancedSettings, {MULTIPLIER_TYPES} from "./AdvancedSettings";
import {useIsMobileHook} from "../utils";

function calculateStk(dmgRange, settings) {
    let stk = 0
    let enemyHealth = settings.enemyHealth - settings.cleanerDamage

    if (settings.multipliers.head > 0) {
        let headshotsToKill = enemyHealth / dmgRange.damages.head
        if (headshotsToKill <= settings.multipliers.head) {
            return Math.ceil(headshotsToKill)
        }
        stk = settings.multipliers.head
        enemyHealth -= stk * dmgRange.damages.head
        // let chestshotsToKill = enemyHealth / dmgRange.damages.chest
        // if (chestshotsToKill <= settings.multipliers.chest) {
        //     return Math.ceil(stk + chestshotsToKill)
        // }
        // stk += settings.multipliers.chest
        // enemyHealth -= settings.multipliers.chest * dmgRange.damages.chest
        let bodyShotsToKill = enemyHealth / dmgRange.damages.body
        return Math.ceil(stk + bodyShotsToKill)
    } else {
        return Math.ceil(enemyHealth / dmgRange.damage);
    }
}

function arrayMax(array) {
    return array.reduce((a, b) => Math.max(a, b), -Infinity);

}

export const LightToggleButton = styled(ToggleButton)({
    '&.MuiToggleButton-root': {
        color: '#aaa',
    },
    '&.MuiToggleButton-root.Mui-selected': {
        color: '#fff',
    },
})

const lineColors = ['blue', 'green', 'yellow', 'cyan', 'pink']
const VictoryZoomVoronoiContainer = createContainer("zoom", "voronoi");

const X_AXIS_MAX = 120;

const Legend = ({savedWeapons, currentWeapon, isMobile}) => {
    const values = []
    if (currentWeapon != null) values.push({name: currentWeapon.name, symbol: {fill: "red"}})
    savedWeapons.forEach((w, i) => values.push({name: w.name, symbol: {fill: lineColors[i]}}))
    return <VictoryLegend x={10} y={0}
                          height={isMobile ? 25 : 18}
                          orientation="horizontal"
                          theme={VictoryTheme.material}
                          gutter={10}
                          style={{labels: {fill: 'white', fontSize: isMobile ? 14 : 9}}}
                          data={values}
    />;
}

function domain(yAxis) {
    return {x: [0, X_AXIS_MAX], y: [yAxis.min, yAxis.max]};
}


function fontSize(isMobile) {
    return isMobile ? 11 : 14;
}

function calculateTtk(weapon, dmgRange, settings) {
    let baseTtk = (60 / (weapon.rateOfFire)) * (calculateStk(dmgRange, settings) - 1);
    let adsTime = settings.adsTtk ? (weapon.adsTime / 1000) : 0;
    let stfTime = settings.stfTtk ? (weapon.sprintToFire / 1000) : 0;
    return baseTtk + Math.max(adsTime, stfTime);

}

function TTKGraph({currentWeapon, savedWeapons, settings, setSettings}) {


    const isMobile = useIsMobileHook()
    const [graphType, setGraphType] = useState('ttk');

    const handleZoom = (domain) => {
        setSelectedDomain(domain)
    }

    const handleBrush = (domain) => {
        setZoomDomain(domain)
    }

    const getYValues = (dmgRange, weapon) => {
        switch (graphType) {
            case 'ttk':
                return calculateTtk(weapon, dmgRange, settings)
            case 'stk':
                return calculateStk(dmgRange, settings)
            case 'damage':
                return dmgRange.damage
        }
    }

    const datapoints = (weapon) => {
        if (weapon == null) return []
        let dataPoints = []
        weapon?.damageRanges.forEach((dmgRange, index) => {
            dataPoints.push({
                x: dmgRange.range,
                y: getYValues(dmgRange, weapon),
                name: weapon.name ?? 'current',
                last: (savedWeapons[savedWeapons.length - 1] === weapon || savedWeapons.length < 1)
            })
        })
        dataPoints.splice(0, 0, {...dataPoints[0], x: 0})
        dataPoints.splice(dataPoints.length - 1, 0, {...dataPoints[dataPoints.length - 1], x: 500})

        let allPoints = []

        if (dataPoints.length > 0) {
            allPoints = [...Array(X_AXIS_MAX + 1).keys()]
            let lastIndex = 0
            for (let i in allPoints) {
                if (i > dataPoints[lastIndex].x) lastIndex++
                allPoints[i] = {...dataPoints[lastIndex], x: parseInt(i)}
            }
        }
        return allPoints;
    }

    const getYAxis = () => {
        let currentDataPoints = datapoints(currentWeapon)
        let savedDataPoints = savedWeapons.map((w) => datapoints(w))
        let datapointsArray = [...savedDataPoints, currentDataPoints]

        switch (graphType) {
            case 'ttk':
                return {
                    label: 'Time to Kill (s)',
                    min: 0,
                    max: Math.max(1, arrayMax(datapointsArray.map((dp) => dp[dp.length - 1]?.y ?? 0)) + 0.1)
                }
            case 'stk':
                return {
                    label: 'Shots to Kill',
                    min: 0,
                    max: Math.max(10, arrayMax(datapointsArray.map((dp) => dp[dp.length - 1]?.y ?? 0)) + 1)
                }
            case 'damage':
                return {
                    label: 'Damage',
                    min: 0,
                    max: Math.max(50, arrayMax(datapointsArray.map((dp) => dp[0]?.y ?? 0)) + 10)
                }
        }
    }

    const [selectedDomain, setSelectedDomain] = useState();
    const [zoomDomain, setZoomDomain] = useState();

    let currentDataPoints = datapoints(currentWeapon)
    let savedDataPoints = savedWeapons.map((w) => datapoints(w))

    const yAxis = getYAxis()

    useEffect(() => {
        let updatedDomain = domain(yAxis);
        if (selectedDomain != null) updatedDomain.x = selectedDomain?.x
        handleBrush(updatedDomain)
    }, [graphType, currentWeapon, savedWeapons, selectedDomain])

    const handleGraphType = (event, newGraphType) => {
        if (newGraphType != null) setGraphType(newGraphType)
    };

    return (
        <Stack>
            <AdvancedSettings settings={settings} setSettings={setSettings}/>
            <ItemPaper sx={{maxHeight: '60vh'}}>
                <Stack sx={{height: '100%'}} alignItems='center' marginTop='10px'>
                    <ToggleButtonGroup
                        value={graphType}
                        exclusive
                        onChange={handleGraphType}
                        aria-label="graphType-select"
                    >
                        <LightToggleButton value="ttk" aria-label="left aligned">
                            Time to Kill
                        </LightToggleButton>
                        <LightToggleButton value="stk" aria-label="centered">
                            Shots to Kill
                        </LightToggleButton>
                        <LightToggleButton value="damage" aria-label="right aligned">
                            Damage Ranges
                        </LightToggleButton>
                        {/*<LightToggleButton value="summary" aria-label="right aligned">*/}
                        {/*    Summary*/}
                        {/*</LightToggleButton>*/}
                    </ToggleButtonGroup>
                    {graphType === 'summary' ?
                        "Summary Here"
                        :
                        <div>
                            <VictoryChart
                                height={isMobile ? 300 : 420}
                                width={isMobile ? 325 : 700}
                                padding={isMobile ? {top: 20, bottom: 40, right: 15, left: 50} :
                                    {top: 20, bottom: 52, right: 15, left: 55}}
                                theme={VictoryTheme.material}
                                domain={{x: [0, X_AXIS_MAX], y: [yAxis.min, yAxis.max]}}
                                containerComponent={
                                    <VictoryZoomVoronoiContainer
                                        zoomDimension="x"
                                        zoomDomain={zoomDomain}
                                        onZoomDomainChange={handleZoom}
                                        voronoiDimension="x"
                                        labels={({datum}) => `${datum.name}\n Range: ${datum.x}m\n${yAxis.label}: ${Math.round(datum.y * 1000) / 1000}${datum.last ? '' : '\n '}`}
                                        labelComponent={<VictoryTooltip constrainToVisibleArea
                                                                        pointerLength={0}
                                                                        cornerRadius={3}
                                                                        labelComponent={
                                                                            <VictoryLabel/>}
                                                                        style={{
                                                                            fontSize: isMobile ? 11 : 12,
                                                                            fill: 'white'
                                                                        }}
                                                                        flyoutStyle={{
                                                                            fill: "#1e2336",
                                                                            strokeWidth: 0.3,
                                                                            stroke: 'white',
                                                                            strokeOpacity: 0.8
                                                                        }}
                                        />}
                                    />
                                }
                            >
                                <VictoryAxis label="Range (m)"
                                             style={{
                                                 axis: {stroke: '#ccc'},
                                                 axisLabel: {
                                                     fontSize: fontSize(isMobile),
                                                     padding: isMobile ? 25 : 30,
                                                     fill: '#fff'
                                                 },
                                                 grid: {stroke: "#ccc", strokeWidth: 0.35, strokeDasharray: '1,0'},
                                                 ticks: {stroke: "#ccc", size: 5},
                                                 tickLabels: {fontSize: fontSize(isMobile), padding: 5, fill: '#fff',}
                                             }}/>
                                <VictoryAxis dependentAxis label={yAxis.label} style={{
                                    axis: {stroke: '#ccc'},
                                    axisLabel: {
                                        fontSize: fontSize(isMobile),
                                        padding: isMobile ? 35 : 40,
                                        fill: '#fff'
                                    },
                                    grid: {stroke: "#ccc", strokeWidth: 0.35, strokeDasharray: '1,0'},
                                    ticks: {stroke: "#ccc", size: 5},
                                    tickLabels: {fontSize: fontSize(isMobile), padding: 5, fill: '#fff',}
                                }}/>
                                <VictoryLine
                                    style={{
                                        data: {stroke: "red", strokeWidth: 1.5},
                                    }}
                                    data={currentDataPoints}
                                    interpolation='stepBefore'
                                />
                                {
                                    savedDataPoints.map((dp, i) =>
                                        <VictoryLine
                                            style={{
                                                data: {stroke: lineColors[i], strokeWidth: 1.5},
                                            }}
                                            data={dp}
                                            interpolation='stepBefore'
                                        />)
                                }
                            </VictoryChart>
                            <Legend savedWeapons={savedWeapons} currentWeapon={currentWeapon} isMobile={isMobile}/>
                        </div>

                    }
                </Stack>
            </ItemPaper>
        </Stack>
    )
}

export default TTKGraph;
